(() => {
  if (customElements.get('mm-search-suggestions')) return;

  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const words = value => normalize(value).match(/[\p{L}\p{N}]+/gu) || [];
  const singular = word => word.length > 3 && word.endsWith('s') ? word.slice(0, -1) : word;
  const key = value => words(value).map(singular).join(' ');
  const stopWords = new Set(['a', 'ao', 'as', 'ate', 'ca', 'com', 'da', 'das', 'de', 'do', 'dos', 'e', 'em', 'na', 'nas', 'no', 'nos', 'o', 'os', 'para', 'por', 'the', 'and', 'of', 'with']);

  // Allow a missing/extra letter or an adjacent transposition, as in "mangtoe".
  function matches(a, b) {
    a = singular(a); b = singular(b);
    if (a === b || (a.length >= 3 && b.startsWith(a))) return true;
    if (a.length < 4 || b.length < 4 || Math.abs(a.length - b.length) > 1) return false;
    if (a.length === b.length) {
      const different = [...a].map((letter, i) => letter === b[i] ? -1 : i).filter(i => i >= 0);
      return different.length === 1 || (different.length === 2 && different[1] === different[0] + 1 && a[different[0]] === b[different[1]] && a[different[1]] === b[different[0]]);
    }
    const longer = a.length > b.length ? a : b, shorter = a.length > b.length ? b : a;
    for (let i = 0; i < longer.length; i++) if (longer.slice(0, i) + longer.slice(i + 1) === shorter) return true;
    return false;
  }

  function suggestions(products, query, limit) {
    const queryWords = words(query).filter(word => !stopWords.has(word));
    const candidates = new Map(), accents = new Map(Object.entries({
      braco: 'braço', protecao: 'proteção', seguranca: 'segurança', dieletrica: 'dielétrica',
      eletrica: 'elétrica', tensao: 'tensão', confortavel: 'confortável', termico: 'térmico',
      nitrilico: 'nitrílico', impermeavel: 'impermeável', oculos: 'óculos'
    }));
    products.forEach(product => {
      for (const word of String(product.title || '').match(/[\p{L}\p{N}]+/gu) || []) {
        const normalized = normalize(word);
        if (!accents.has(normalized)) accents.set(normalized, word);
      }
    });
    const coverage = label => {
      const labelWords = words(label);
      return queryWords.length ? queryWords.filter(word => labelWords.some(candidate => matches(word, candidate))).length / queryWords.length : 0;
    };
    const add = (label, kind, rank, type) => {
      label = String(label || '').trim().replace(/\s+/g, ' ');
      const normalized = key(label);
      if (!normalized || normalized === key(query) || label.length > 64 || /[<>=_:#|]/.test(label) || words(label).length > 9 || !/[\p{L}]/u.test(label)) return;
      const existing = candidates.get(normalized);
      if (existing) { existing.ranks.add(rank); existing.count = existing.ranks.size; if (type) existing.types.add(type); return; }
      candidates.set(normalized, { label, kind, count: 1, ranks: new Set([rank]), types: new Set(type ? [type] : []), rank, relevance: coverage(label) });
    };
    products.forEach((product, rank) => {
      const seen = new Set();
      for (const tag of Array.isArray(product.tags) ? product.tags : []) {
        if (seen.has(key(tag))) continue;
        seen.add(key(tag));
        // Recover spelling/accents from the matching catalog titles, never from HTML.
        const label = String(tag).replace(/[\p{L}\p{N}]+/gu, word => {
          const spelling = accents.get(normalize(word)) || word;
          return /^[A-Z\d]{1,5}$/.test(spelling) ? spelling : spelling.toLocaleLowerCase('pt-BR');
        });
        add(label.charAt(0).toLocaleUpperCase('pt-BR') + label.slice(1), 'tag', rank, product.type);
      }
      if (product.type && !/^(default|generic|geral|produto|product)$/i.test(product.type.trim())) {
        add(product.type, 'category', rank, product.type);
        if (product.vendor) add(`${product.type} ${product.vendor}`, 'brand', rank, product.type);
      }
    });
    const ranked = [...candidates.values()].sort((a, b) => b.relevance - a.relevance || b.count - a.count || a.rank - b.rank || a.label.length - b.label.length);
    const relevant = ranked.some(item => item.relevance > 0) ? ranked.filter(item => item.relevance > 0) : ranked;
    const selected = [];
    const groups = [...new Set(products.map(product => product.type).filter(Boolean))];
    // A broad search such as "luva" should reflect several matching categories.
    for (const group of groups.slice(0, Math.min(4, limit - 1))) {
      const candidate = relevant.find(item => item.types.has(group) && !selected.includes(item));
      if (candidate) selected.push(candidate);
    }
    for (const candidate of relevant) {
      if (selected.length === limit) break;
      if (!selected.includes(candidate)) selected.push(candidate);
    }
    // Keep one matching category/brand when many nearly identical tags dominate.
    const brand = relevant.find(item => item.kind === 'brand' && item.relevance >= (selected[0]?.relevance || 0));
    if (brand && selected.length >= 3 && !selected.includes(brand)) selected[selected.length - 1] = brand;
    if (!selected.length && products[0]?.title) {
      const title = products[0].title.split(/\s+/).slice(0, 6).join(' ');
      selected.push({label: title});
    }
    return selected.map(item => item.label);
  }

  class SearchSuggestions extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      const source = this.querySelector('[data-search-suggestions]');
      if (!source) return;
      let data;
      try { data = JSON.parse(source.textContent); } catch (_) { return; }
      this.initialized = true;
      const rawQuery = String(data.terms || '').trim();
      const delimiter = rawQuery.lastIndexOf(' AND ');
      const prefix = delimiter >= 0 ? rawQuery.slice(0, delimiter) : '';
      const productType = (data.products || []).some(product => key(product.type) === key(prefix)) ? prefix : '';
      const query = productType ? rawQuery.slice(delimiter + 5) : rawQuery;
      const limit = Math.min(8, Math.max(3, Number(this.dataset.limit) || 6));
      const labels = suggestions(data.products || [], query, limit);
      const list = this.querySelector('ul'), icon = this.querySelector('[data-search-icon]');
      list.replaceChildren();
      for (const label of labels) {
        const url = new URL(this.dataset.searchUrl || '/search', window.location.origin);
        url.searchParams.set('q', label);
        url.searchParams.set('type', 'product');
        url.searchParams.set('options[prefix]', 'last');
        if (productType) url.searchParams.set('filter.p.product_type', productType);
        const li = document.createElement('li'), button = document.createElement('custom-button'), link = document.createElement('a');
        link.href = url.pathname + url.search;
        link.className = 'mm-search-suggestions__link search_heading predictive-search-item rounded-corners-md text-medium anm-button-secondary';
        const symbol = document.createElement('span');
        symbol.className = 'mm-search-suggestions__icon';
        symbol.setAttribute('aria-hidden', 'true');
        if (icon) symbol.append(icon.content.cloneNode(true));
        const text = document.createElement('span');
        text.textContent = label;
        link.append(symbol, text); button.append(link); li.append(button); list.append(li);
      }
      this.hidden = !labels.length;
      this.keyboardController = new AbortController();
      const options = {signal: this.keyboardController.signal};
      list.addEventListener('keydown', event => {
        if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
        const links = [...list.querySelectorAll('a')], index = links.indexOf(document.activeElement);
        if (index < 0) return;
        event.preventDefault();
        links[(index + (event.key === 'ArrowDown' ? 1 : -1) + links.length) % links.length].focus();
      }, options);
    }
    disconnectedCallback() {
      this.keyboardController?.abort();
      // Cached predictive results create new elements; reconnection of this one is safe too.
      this.initialized = false;
      this.querySelector('ul')?.replaceChildren();
    }
  }
  customElements.define('mm-search-suggestions', SearchSuggestions);
})();
